import * as PIXI from "pixi.js";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import GambleLevelOverlay, {
  TIER_LEVELS,
  TIER_SKINS,
  FS_NUMBER_OFFSET_Y,
  FS_NUMBER_BONE_SCALE,
  makeNumberTextStyle,
  pipAnimationsForTier,
} from "./GambleLevelOverlay";
import { Settings } from "../../../settings";
import {
  symbolData,
  SCATTER_BLANK_SYMBOL_ID,
} from "../../../SymbolData";
import { SOUND_NAME } from "../../../sounds";
// The Golden Mystery's OWN motion — see the strip block below.
import { goldenStripMotion } from "../Mystery/goldenMysteryConfig";

/**
 * Gamble scatter (SC1) — the config that drives RevealSymbolController.
 *
 * The host is the `scg` spine: it lands, its doors slide open, and a strip of
 * free-spin packages spins inside it until it settles on the one the backend
 * awarded. Everything game-specific lives here so the controller stays generic.
 *
 * Strip cells are FLAT TEXTURES, captured once per skin from the
 * `gamble_level_symbol` spine — the same trick the reels use, where every
 * spinning symbol is a texture pulled off its spine at load time. A spine per
 * cell would be four skeletons ticking for something the eye only sees blur.
 */

// ── Packages (GDD §3.3 and §6) ──────────────────────────────────────────────
//
// The API reports an outcome as [spinCount, tier]. Tier codes seen live are
// 0 / 1 / 3 / 5, and each maps to one colour skin with its own spin range:
//
//   tier 1  color/low   purple  Level 0  4-9 spins
//   tier 3  color/mid   red     Level 2  7-9 spins
//   tier 5  color/high  gold    Level 4  8-10 spins
//   tier 0  blank, no award
//
// Tier 0 needs no skin. GDD §3.3 calls it "SC empty — empty frame, container
// state", and the `bl` spine is exactly that: the scatter frame with a mask
// and an empty attachment_slot_symbol, the same structure as `scg` minus the
// doors. A blank is the WINDOW WITH NOTHING IN IT, so the strip lands on a
// cell that draws no art and no number. Nothing to add, nothing missing.
export const BLANK_TIER = 0;

// `staticSymbolId` is BLANK-ONLY now. An awarded tier's settled cell is the
// composite capture (see toStaticCell), which has no symbolData id; the blank
// is a real picture of a real outcome — `bl`, the empty window — so it keeps
// one.
export const GAMBLE_TIERS = {
  [BLANK_TIER]: {
    skin: null,
    values: [0],
    staticSymbolId: SCATTER_BLANK_SYMBOL_ID,
  },
  1: {
    skin: TIER_SKINS[1],
    values: [4, 5, 6, 7, 8, 9],
  },
  3: {
    skin: TIER_SKINS[3],
    values: [7, 8, 9],
  },
  5: {
    skin: TIER_SKINS[5],
    values: [8, 9, 10],
  },
};

const STRIP_SPINE = "gamble_level_symbol";

/**
 * The blank strip cell's picture — symbol 17 (`bl`), the same one the reel
 * keeps when a blank LANDS.
 *
 * Deliberately the reel's own extracted texture rather than a second capture
 * of the same spine: the spinning blank and the settled blank are then
 * literally one texture and cannot drift apart, which is the entire point.
 * The name comes off SymbolData so a re-key there cannot silently miss.
 */
const blankStripTexture = () => {
  const name = symbolData[SCATTER_BLANK_SYMBOL_ID]?.texture;
  if (!name) return null;
  return SpineTextureExtractor.getSymbols()?.textures?.[`${name}.png`] ?? null;
};

// Every (tier, value) pair the strip may show while spinning. Built once —
// the pool is small and fixed, so there is no reason to compute it per spin.
// The blank IS in the pool: it is a real outcome the strip can land on
// (seen live 2026-08-18, round 1420795 — 2×SC + SC1, no award), and a
// possibility the spin should show the player it is risking.
const buildPool = () => {
  const pool = [];
  for (const [tier, cfg] of Object.entries(GAMBLE_TIERS)) {
    for (const value of cfg.values) {
      pool.push({ tier: Number(tier), value });
    }
  }
  return pool;
};

export const GAMBLE_STRIP_ITEMS = buildPool();

// The number's placement is NOT chosen here — it is copied from the spine's
// own `fs_number` bone so the flat cell and the live card put it in exactly
// the same place. See FS_NUMBER_* in GambleLevelOverlay.

/**
 * An empty strip cell: one sprite for the coloured package, one text for the
 * spin count. Both are reused across renders — a spinning strip must never
 * allocate per frame.
 */
const createCell = () => {
  const cell = new PIXI.Container();
  cell.name = "gamble_level_cell";

  const sprite = new PIXI.Sprite();
  sprite.anchor.set(0.5);
  sprite.name = "gamble_level_art";
  cell.addChild(sprite);

  const text = new PIXI.Text("", makeNumberTextStyle(1));
  text.anchor.set(0.5);
  text.name = "gamble_level_number";
  cell.addChild(text);

  cell.__art = sprite;
  cell.__number = text;
  return cell;
};

/**
 * Build a renderCell bound to a cell size. The controller does not know how a
 * cell is drawn, so the sizing has to be closed over here.
 */
// Size is a PARAMETER, never captured. The spinner passes its current cell
// size on every draw so a resize cannot leave the strip out of step with the
// spine that replaces it.
export const makeRenderCell = () => {
  return (cell, item, cellWidth, cellHeight) => {
    if (!item) return;
    const tierCfg = GAMBLE_TIERS[item.tier];
    if (!tierCfg) return;

    // ── Blank — the empty WINDOW, drawn at full size ──
    //
    // It used to set Texture.EMPTY, on the reading that "a blank is the window
    // with nothing in it" — but that put nothing in the CELL, which is not the
    // same statement. A blank spinning past was a gap showing the scatter's own
    // backing through it, while a blank that LANDED showed `bl`: a full-size
    // framed tile. The same outcome looked like two different things depending
    // on whether it was moving.
    //
    // `bl` is what a blank actually is — 242x242 frame and backing under the
    // same 1.3 scale bone as every package card, so it fills the cell exactly
    // as they do. Drawn by the same rule for the same reason.
    if (!tierCfg.skin) {
      const blankTexture = blankStripTexture();
      const blankSprite = cell.__art;
      if (blankSprite) {
        blankSprite.texture = blankTexture || PIXI.Texture.EMPTY;
        blankSprite.scale.set(cellHeight / Settings.SYMBOL_HEIGHT);
      }
      if (cell.__number) cell.__number.text = "";
      return;
    }

    // The pips are baked into the captured frame, so a package is already
    // wearing its level while it is still spinning past — the card the strip
    // shows and the card that lands are then the same picture.
    const texture = SpineTextureExtractor.getSkinTexture(
      STRIP_SPINE,
      tierCfg.skin,
      pipAnimationsForTier(item.tier),
    );

    const sprite = cell.__art;
    if (sprite && texture) {
      sprite.texture = texture;
      // EXACTLY the rule the reels use — containerHeight / SYMBOL_HEIGHT, with
      // no reference to the texture's own size. Fitting to the texture made
      // the spinning card smaller than the spine that replaces it, because the
      // captured frame includes effect art that extends past the card.
      sprite.scale.set(cellHeight / Settings.SYMBOL_HEIGHT);
    }

    const text = cell.__number;
    if (text) {
      text.text = String(item.value);
      // Per-tier style, same source the live card uses.
      text.style = makeNumberTextStyle(item.tier);
      // Reproduce what the bone would have done: its offset, and the 1.3 it
      // passes down to whatever the slot holds.
      const scale = cellHeight / Settings.SYMBOL_HEIGHT;
      text.scale.set(scale * FS_NUMBER_BONE_SCALE);
      text.position.set(0, -FS_NUMBER_OFFSET_Y * scale);
    }
  };
};

/**
 * The full config handed to RevealSymbolController.
 *
 * Animation and slot names are candidate LISTS on purpose. Three separate bugs
 * in this game came from a spine re-export renaming something and the old
 * single name silently resolving to nothing.
 */
export const buildGambleScatterConfig = (cellWidth, cellHeight) => ({
  slotNames: ["attachment_slot_symbol"],

  // No PIXI mask. The Graphics rect rendered as a solid white block instead of
  // clipping — a mask that is also a child of the container it masks is not
  // reliably honoured once the object is parented by addSlotObject. The `scg`
  // spine carries its own clipping attachment on the `mask` slot, so try that
  // first; if the strip is seen to overflow the frame, the fix is a mask on
  // the holder's PARENT, not on the holder.
  mask: false,

  // The doors, as a sound. This is the ONE reveal in the game the player hears
  // open — RevealSymbolController fires it only because this config names it,
  // so the plain mystery lid next door stays silent.
  openSound: SOUND_NAME.scatter_door,

  // What the doors TAKE OVER FROM. The regular symbols' win plays first and on
  // its own; the moment the doors start opening it is faded out, so the two are
  // heard in sequence and never on top of each other.
  cutSound: SOUND_NAME.basic_win,

  // The strip behind those doors, as a loop. Named here and nowhere else: the
  // Golden Mystery spins a strip too and several of its cells spin at once,
  // which is not a thing the player is meant to hear.
  spinSound: SOUND_NAME.scatter_spin,

  sequence: {
    // NOTHING. The symbol has already arrived by the time this runs.
    //
    // useLandingAnimations bounces every landed symbol when the reel stops,
    // this one included, so a `land` clip here is a SECOND bounce a beat
    // later. Empty means play() skips straight to injecting the strip and
    // opening, which is also what keeps the window from being empty when the
    // doors part.
    //
    // NOT `win` either — the trigger's celebration is held back until the
    // strip has landed, and then all three scatters play it together. See
    // RevealSymbolController.playWin.
    land: [],
    open: ["reveal"],
    loop: ["anticipation"],
    // The resting pose once the strip has landed.
    //
    // `revealed_static` is the open, DECIDED window. It replaces the
    // `anticipation` loop, which used to be left running after the outcome was
    // already known — so the scatter kept glowing, and on a LOSS that read as
    // a pending state for something that had already finished.
    //
    // It is zero-length, and _playHold LOOPS it instead of playing it once.
    // That is the whole trick. A zero-length one-shot completes on the same
    // frame, its track ends, and Spine reverts the skeleton to the setup pose
    // — doors shut. Looped, it holds its single frame for ever, and that is
    // safe because the runtime guards the wrap:
    //
    //   spine-core Animation.js  ->  if (loop && this.duration != 0)
    //
    // so a 0-duration loop applies the frame rather than dividing by it.
    //
    // NOT `static`, which is the CLOSED pose and would shut the doors the
    // sequence just opened.
    //
    // A WON package overrides this a moment later — playRevealWin puts `win`
    // on the same track — so in practice this is the pose a BLANK ends on.
    settle: ["revealed_static"],

    // Played only when the caller asks, AFTER the strip has landed — see
    // RevealSymbolController.playWin. The trigger's celebration is held back
    // to that moment so all three scatters fire together, and it LOOPS,
    // because a trigger line runs until the intro dialog takes the screen.
    win: ["win"],
  },

  // Change to the settle pose on the anticipation loop's OWN boundary, not the
  // instant the strip stops.
  //
  // The glow is authored 0 -> peak -> 0 across the loop's 0.5s, so at the
  // boundary it is already invisible and the swap cannot be seen. Landing the
  // swap wherever the strip happened to finish cut the glow off mid-pulse.
  //
  // The cost is up to 0.5s of extra hold before the reveal promise resolves,
  // which also delays the celebration — wanted, since it puts that on the seam
  // too rather than on top of a half-faded glow.
  settleOnLoopComplete: true,

  // How long the strip free-spins before it starts easing onto the result.
  // Anticipation loops for exactly this long, so it is the dial for how much
  // tension the reveal carries.
  //
  // ── THE FIRST LANDING ONLY ──
  //
  // Shorter than the Golden Mystery's 2600 on purpose. That number is for a
  // whole board unfolding; this is one cell answering one question, and it sits
  // in the middle of a base-game spin the player is waiting on. Vladimir,
  // 2026-09-22: *"only first is shorter and base game one."*
  //
  // Every RE-ROLL ignores this and takes the coins' length and stagger instead,
  // so they start and finish with the wave — see RevealSymbolController.respin.
  spinDurationMs: 1600,

  // Landing swaps the flat strip texture for the live card, which is what
  // plays the level pips. The strip's own cells are hidden rather than
  // destroyed — the spinner still owns them.
  onLanded: ({ parent, item, cellWidth, cellHeight, strip, settled = false }) => {
    // Spine FIRST, texture hidden ONE FRAME LATER.
    //
    // This is the order every landing in the engine uses — see
    // useLandingAnimations, which shows the overlay animation and then hides
    // the reel texture inside a requestAnimationFrame. Its comment says why:
    // "allows spine GPU textures to upload before texture disappears. The
    // 1-frame overlap is invisible since both show the same symbol image."
    //
    // Hiding the strip in the same frame as creating the spine does the exact
    // opposite — there is a frame with neither on screen, which is the flash.
    const overlay = new GambleLevelOverlay({
      parent,
      item,
      cellWidth,
      cellHeight,
      // THE landing. This is the one caller that is a real arrival — the other
      // two build the card only to photograph it at rest. Except showSettled
      // (the free-spins room): it builds the same card as a copy of one that
      // already landed, so it asks for the resting card.
      landing: !settled,
    });
    requestAnimationFrame(() => {
      if (strip?.container && !strip.destroyed) {
        strip.container.visible = false;
      }
    });
    return overlay;
  },

  strip: {
    // ── The Golden Mystery's motion, not a copy of it ──
    //
    // This block used to declare no motion at all, so the strip silently took
    // SymbolStripSpinner's class defaults: speed 46 against the coins' 130, an
    // overshoot of 1.1 where they have none, no wind-up, and no measured
    // landing handover. That was never a decision — it is what you get by not
    // choosing — and it is why the scatter read as a different feature spinning
    // beside the coins, and why its landing felt wrong next to theirs.
    //
    // ONE mechanism now. Only the DURATION differs, and it is stated above.
    //
    // The motion only: `goldenStripMotion` leaves the coins' `items` and
    // `warmLabels` behind, because those are their contents and not their
    // movement.
    ...goldenStripMotion(),

    items: GAMBLE_STRIP_ITEMS,
    // ── The pool is THE LANDED TIER AND ABOVE — not `variants` ──
    //
    // This used to be built from the round's `variants`, on the reading that a
    // strip should come from the data rather than a table. The data was real;
    // it was the wrong data. `variants` is "every pair the NEXT GAMBLE can
    // produce" — it shrinks as the ladder climbs and it EXCLUDES what is
    // already held. Straight off a live round:
    //
    //   values [[9,1],[7,3]]   variants [[0,0],[8,5],[9,5],[10,5]]
    //
    // Holding red at tier 3, `variants` is blank and gold only. So the belt
    // spun gold and blank and then landed red — a card that was never in the
    // pool. And it was not a bought-round quirk: a natural purple trigger has
    // `variants` of red + gold + blank, so that landed out of nowhere too. The
    // strip was asking the GAMBLE's question and answering the SCATTER's.
    //
    // ── What it shows now ──
    //
    //   landed purple -> purple · red · gold · blank
    //   landed red    ->          red · gold · blank
    //   landed gold   ->                gold · blank
    //   landed blank  -> every tier, because any of them could have been
    //
    // Three things fall out of that. The landed card is ALWAYS in the pool, so
    // nothing arrives from nowhere. Nothing BELOW the award is shown — a
    // near-miss on something worse carries no tension. And the spin reads as
    // the question it actually is: how high did I get.
    //
    // The four outcomes are the GDD's own, from §5's symbol table and repeated
    // in §8: purple 4-9, red 7-9, gold 8-10, and BLANK. The GDD describes no
    // spinning strip at all, so the pool is a presentation decision — this is
    // it, written down.
    //
    // The BLANK always rides along: it is a real landing (seen live 2026-08-18,
    // round 1420795 — 2xSC + SC1, no award), and the spin should show the
    // nothing it is risking.
    //
    // `items` stays the fallback for a result with no tier at all.
    buildItems: (result) => {
      const landed = Number(result?.tier);
      if (!Number.isFinite(landed) || !GAMBLE_TIERS[landed]) return null;

      const pool = [];
      for (const [key, cfg] of Object.entries(GAMBLE_TIERS)) {
        const tier = Number(key);
        // The blank is not a rung — it is the floor, and it is always in.
        if (tier !== BLANK_TIER && tier < landed) continue;
        for (const value of cfg.values) pool.push({ tier, value });
      }

      // ── The landed VALUE, guaranteed ──
      //
      // The tier's own value list is the GDD's band, and the server is the
      // authority on the number: §16.10 is an open question about whether
      // purple really spans 4-9, and a band that disagrees with the payload
      // would put the belt back to landing on something it never showed. So the
      // awarded pair is added when the table does not already carry it.
      const value = Number(result?.value);
      if (
        landed !== BLANK_TIER &&
        Number.isFinite(value) &&
        !pool.some((item) => item.tier === landed && item.value === value)
      ) {
        pool.push({ tier: landed, value });
      }

      return pool.length ? pool : null;
    },
    /**
     * What the window is showing the instant the doors open.
     *
     * ── Never the blank ──
     *
     * The strip fills every cell at random, so the very first thing a player
     * saw when the doors parted could be the empty frame — the one outcome that
     * awards nothing. Opening on it reads as the answer rather than as a
     * starting point, and the spin that follows feels like a recovery.
     *
     * So the opening frame is drawn from the awarding tiers only. The blank
     * stays in the POOL and can still scroll past and still land — it is a real
     * outcome (GDD §8, §3.3) and hiding it would be a lie about the odds. This
     * is about the first frame, and nothing else.
     *
     * Random among them rather than the best one: the window should promise
     * something, not promise the top.
     *
     * @param   {Array} items The pool the strip was built with.
     * @returns {object|null} null leaves the strip's own random pick alone.
     */
    openItem: (items) => {
      const awarding = (items || []).filter(
        (item) => item && item.tier !== BLANK_TIER,
      );
      if (!awarding.length) return null;
      return awarding[Math.floor(Math.random() * awarding.length)];
    },
    createCell,
    renderCell: makeRenderCell(),
    // Pot Collection AUTO values, from
    // FEATURES_CONFIG.POT_COLLECTION.AUTO_PLAY_SPEEDS.AUTO. That feature is the
    // only other place in this engine that spins ONE cell, and its feel is the
    // reference here — the base game's numbers are tuned for a six-high reel
    // and read far too quick over a single cell.
    //
    //   SPEED_MS_PER_SYMBOL_HEIGHT 120   ("deliberate bonus spin feel")
    //   ACCELERATION_DURATION      200
    //   BOUNCE_OVERSHOOT           1.1
    //   LANDING_DURATION           375   FIXED, not velocity matched
    //
    // The fixed landing is the part that matters. PotCollectionSymbolController
    // takes LANDING_DURATION when it is present and only falls back to the
    // velocity formula when it is absent. Deriving it instead gave ~131ms,
    // which is why the bounce read as far too fast.
    // ── These used to live here, and they are why it never matched ──
    //
    //   speedMs: 170          the coins run 130
    //   accelMs: 320          the same, by coincidence
    //   overshoot: 1.25       the coins have NONE — this is the bounce
    //   stopMs: 560           fixed, where the coins derive it from velocity
    //   windUpDistance: 0.22  the same
    //   windUpMs: 300         the same
    //
    // Tuned in isolation, against Pot Collection's numbers, for a reveal that
    // was thought of as a one-off event. It is not one: it opens in the middle
    // of a Golden Mystery and re-rolls inside a board of coins, so every one of
    // these differences read as a second feature spinning beside the first.
    //
    // Gone, deliberately, rather than edited to match — matching by hand is how
    // they drifted in the first place. The spread at the top of this block is
    // the only statement of motion now. Only the DURATION is this symbol's own,
    // and it is stated outside the block.
  },
});

/**
 * Turn what the backend sent into the item the strip lands on.
 *
 * Live shape (round 1415621):
 *   features.fs_gamble.values         [[9, 1]]
 *   outcome.storage.gamble_scatter_values  [[3,0], 9, 1]
 *
 * Both carry the pair as [spinCount, tier]. Tier 0 is a legitimate outcome and
 * lands on an empty window; only an unknown tier returns null.
 */
export const toStripItem = (gambleScatter) => {
  if (!gambleScatter) return null;
  const { tier, value, variants } = gambleScatter;
  if (!GAMBLE_TIERS[tier]) return null;
  // ── What the sequence is allowed to see ──
  //
  // This is the only thing handed to RevealSymbolController.play(), so anything
  // dropped here is invisible to the whole sequence.
  //
  // `tier` and `value` are what the strip is built from — see buildItems, which
  // spins the landed tier and everything above it.
  //
  // `variants` is NO LONGER read by the strip, and that is the fix rather than
  // an oversight: it describes what the NEXT GAMBLE can produce, so using it
  // here made the belt spin one set and land on another. It stays on the object
  // because the reveal hands the same payload to the gamble screen, which is
  // the one place the word means what it says.
  //
  // Listed explicitly rather than spread, on purpose: this is a contract, and a
  // new field the sequence needs should have to be added here deliberately.
  return { tier, value, variants };
};

/**
 * The static reel texture this outcome must leave behind.
 *
 * The reveal plays on the OVERLAY. Underneath it ReelControllerV2 still holds
 * the `scg` texture, and `scg` has one skin whose pose is the CLOSED doors —
 * the gold card face. So when the win controller hides the overlay and calls
 * showAllSymbolTextures on the next spin, the empty window the player just
 * watched land is replaced by a shut gold door.
 *
 * The cure is the one the expanding wild already uses: swap the hidden texture
 * to match the result, so the cell carries the blank forward.
 *
 * EVERY tier does this, not just the blank. A won package usually goes straight
 * to the gamble screen and the board is rebuilt before the base game is seen
 * again — but "usually" is not "always" (a reload, a replay, a collect that
 * returns to base), and a cell that lies about what it holds is a bug waiting
 * for one of those paths.
 *
 * @param   {{tier:number,value:number}|null} item  as toStripItem returns it.
 * @param   {{pips:number}} [options] `pips: 0` for the spent card.
 * @returns {{symbolId:number, texture:PIXI.Texture|null, label:Object|null}|null}
 *          what the cell must hold, or null to leave it alone.
 */
/**
 * The pip state to capture, normalised.
 *
 * A tier with no pips has ONE card, not two — asking for "spent" purple would
 * otherwise mint a second texture identical to the first under a different
 * cache key, and warmRevealedCards could never pre-build the one the game then
 * asks for. Collapsing it here keeps the boot set and the runtime lookups in
 * exact agreement.
 *
 * @param   {number} tier
 * @param   {number|null|undefined} pips
 * @returns {number|null} null means "the tier's own count".
 */
const revealedCardPips = (tier, pips) => {
  if (pips === null || pips === undefined) return null;
  return (TIER_LEVELS[tier] ?? 0) > 0 ? pips : null;
};

/**
 * Build every settled-reveal texture the game can ever need, at boot.
 *
 * ── Why this is not lazy ──
 *
 * A landing symbol is not known before the API answers either, and the reel
 * does not wait to find out: SpineTextureExtractor makes one texture per
 * symbol at load and the round simply picks. This is the same set-and-pick
 * problem, and the set is just as finite — every (tier, value) pair the math
 * can award is in GAMBLE_TIERS, and each card has at most two pip states.
 *
 *   purple  6 values × 1 state   (no pips, so lit and spent are one card)
 *   red     3 values × 2 states
 *   gold    3 values × 2 states
 *   ───────────────────────────
 *   18 textures
 *
 * Built here rather than on the reveal, because the reveal is the one moment
 * the player is watching a timed animation and a weak device can least afford
 * two skeleton instantiations and a render. At boot it is 18 more of exactly
 * the work that is already being done for every symbol on the board.
 *
 * Everything lands in the same cache the runtime lookup reads, so a miss —
 * a value the math awards that this table does not list — still works. It just
 * costs what it used to.
 */
export const warmRevealedCards = () => {
  for (const [tierKey, cfg] of Object.entries(GAMBLE_TIERS)) {
    // The blank has no card at all — an empty window is the `bl` texture the
    // reel already owns.
    if (!cfg.skin) continue;

    const tier = Number(tierKey);
    const states = (TIER_LEVELS[tier] ?? 0) > 0 ? [null, 0] : [null];

    for (const value of cfg.values) {
      for (const pips of states) {
        SpineTextureExtractor.getRevealedCardTexture(
          { tier, value },
          (args) => new GambleLevelOverlay(args),
          pips,
        );
      }
    }
  }
};

export const toStaticCell = (item, options = {}) => {
  const tierCfg = GAMBLE_TIERS[item?.tier];
  if (!tierCfg) return null;

  // The blank has no card and no count — an empty window shows nothing, and
  // `awarded: false` is what tells the caller there is no win to present.
  if (!tierCfg.skin) {
    return { symbolId: tierCfg.staticSymbolId, awarded: false };
  }

  // ── The whole reveal, as one picture ──
  //
  // The open gold window with the card inside it, its pips lit and its spin
  // count in the skeleton's own text slot — captured from exactly the objects
  // RevealSymbolController.showSettled puts on screen. See
  // SpineTextureExtractor.getRevealedCardTexture.
  //
  // The cell used to keep the bare card and paint the number on top of it,
  // reproducing the `fs_number` bone chain out here by hand. That is what the
  // label below was, and why the settled cell had no window around it.
  //
  // `pips` decides WHICH of the two states this is. Lit is what the player
  // wins and what stands on the base-game reel. Spent — 0 — is the card after
  // the entry flourish has fired its pips into the level boxes, and it is the
  // one the next free spin carries away. See pipCountFor.
  const texture = SpineTextureExtractor.getRevealedCardTexture(
    item,
    (args) => new GambleLevelOverlay(args),
    revealedCardPips(item.tier, options.pips),
  );

  return {
    // ── ONE path. The composite, or nothing. ──
    //
    // There used to be a fallback here: the bare card (symbol 18/19/20) with
    // the spin count drawn on top as a separate PIXI.Text, positioned by
    // reproducing the skeleton's `fs_number` bone chain by hand. That was the
    // FIRST attempt at this cell, and once the composite worked it became a
    // second path that could never run in normal play — so it would rot
    // unnoticed, and it was already the version that did not look right.
    //
    // Deleted deliberately. If a capture ever fails the cell keeps the closed
    // scatter, which is visibly wrong and therefore gets fixed, rather than
    // silently degrading to art nobody has looked at in weeks.
    texture,
    // A package was won. Stated HERE because this table owns what a tier means
    // — the caller must not have to know that tier 0 is the losing one.
    awarded: true,
  };
};
